import { getSupabaseConfig } from "./config";

const FORBIDDEN_PROMPT_KEYS = new Set([
  "prompt_id",
  "locket_id",
  "owner_user_id",
  "beloved_identity_id",
  "token_id",
  "token_hash",
  "id",
]);

export type StorybookPromptPayload = {
  prompt_text: string;
  prompt_category?: string | null;
  recipient_label?: string | null;
  beloved_display_name?: string | null;
  expires_at?: string | null;
  consent_text_version?: string | null;
  allow_voice?: boolean;
  owner_first_name?: string | null;
};

export type StorybookApiErrorCode =
  | "missing_config"
  | "network"
  | "invalid_token"
  | "token_expired"
  | "token_revoked"
  | "token_completed"
  | "token_invalid"
  | "rate_limit"
  | "consent_required"
  | "content_required"
  | "response_exists"
  | "unavailable"
  | "unknown";

export class StorybookApiError extends Error {
  readonly code: StorybookApiErrorCode;

  constructor(code: StorybookApiErrorCode, message?: string) {
    super(message ?? code);
    this.code = code;
  }
}

function assertSafePromptPayload(data: unknown): StorybookPromptPayload {
  if (!data || typeof data !== "object") {
    throw new StorybookApiError("unavailable");
  }
  const obj = data as Record<string, unknown>;
  for (const key of Object.keys(obj)) {
    if (FORBIDDEN_PROMPT_KEYS.has(key)) {
      throw new StorybookApiError("unavailable");
    }
  }
  if (typeof obj.prompt_text !== "string" || !obj.prompt_text.trim()) {
    throw new StorybookApiError("unavailable");
  }
  return obj as StorybookPromptPayload;
}

function mapPostgrestError(message: string): StorybookApiErrorCode {
  const m = message.toLowerCase();
  if (m.includes("storybook_token_expired")) return "token_expired";
  if (m.includes("storybook_token_revoked")) return "token_revoked";
  if (m.includes("storybook_token_completed")) return "token_completed";
  if (m.includes("storybook_token_invalid")) return "token_invalid";
  if (m.includes("storybook_token_max_responses")) return "token_completed";
  if (m.includes("storybook_response_already_exists")) return "response_exists";
  if (m.includes("storybook_rate_limit")) return "rate_limit";
  if (m.includes("storybook_consent_required")) return "consent_required";
  if (m.includes("storybook_response_content_required")) return "content_required";
  return "unavailable";
}

async function rpc<T>(fn: string, body: Record<string, unknown>): Promise<T> {
  const { url, anonKey } = getSupabaseConfig();
  let res: Response;
  try {
    res = await fetch(`${url}/rest/v1/rpc/${fn}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: anonKey,
        Authorization: `Bearer ${anonKey}`,
      },
      body: JSON.stringify(body),
    });
  } catch {
    throw new StorybookApiError("network");
  }

  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = null;
    }
  }

  if (!res.ok) {
    const msg =
      parsed &&
      typeof parsed === "object" &&
      "message" in parsed &&
      typeof (parsed as { message: unknown }).message === "string"
        ? (parsed as { message: string }).message
        : "request_failed";
    throw new StorybookApiError(mapPostgrestError(msg));
  }

  return parsed as T;
}

async function sha256Hex(input: string): Promise<string> {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(input));
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/** Session fingerprint for rate limits — not the invite token. */
async function clientFingerprint(): Promise<{ ipHash: string; uaHash: string }> {
  const ua = navigator.userAgent.slice(0, 256);
  const salt = "storybook-web-v1";
  const [ipHash, uaHash] = await Promise.all([
    sha256Hex(`${salt}:fp:${ua}`),
    sha256Hex(`${salt}:ua:${ua}`),
  ]);
  return { ipHash, uaHash };
}

export async function fetchExternalPrompt(
  rawToken: string
): Promise<StorybookPromptPayload> {
  const { ipHash, uaHash } = await clientFingerprint();
  const data = await rpc<unknown>("storybook_external_fetch_prompt", {
    p_raw_token: rawToken,
    p_ip_hash: ipHash,
    p_user_agent_hash: uaHash,
  });
  return assertSafePromptPayload(data);
}

export async function submitExternalResponse(
  rawToken: string,
  typedText: string,
  consentTextVersion: string,
  clientSubmissionId: string
): Promise<void> {
  const { ipHash, uaHash } = await clientFingerprint();
  const result = await rpc<{ ok?: boolean }>("storybook_external_submit_response", {
    p_raw_token: rawToken,
    p_typed_text: typedText,
    p_consent_acknowledged: true,
    p_consent_text_version: consentTextVersion,
    p_client_submission_id: clientSubmissionId,
    p_ip_hash: ipHash,
    p_user_agent_hash: uaHash,
  });
  if (!result || result.ok !== true) {
    throw new StorybookApiError("unknown");
  }
}
